"use client";

import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquare } from "lucide-react";
import { useChatStore } from "@/lib/chat-store";
import { useOverlayStore } from "@/lib/overlay-store";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { ConversationList } from "./ConversationList";
import { ChatView } from "./ChatView";

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const panelVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 400,
      damping: 35,
      mass: 0.8,
    },
  },
  exit: {
    opacity: 0,
    y: 40,
    scale: 0.96,
    transition: {
      duration: 0.2,
      ease: "easeInOut" as const,
    },
  },
};

export function ChatWindow() {
  const { isOpen, closeChat, activeConversationId, setActiveConversation } =
    useChatStore();

  // Lock body scroll when chat is open
  useScrollLock(isOpen);

  // Register overlay (hide dock)
  const { registerOverlay, unregisterOverlay } = useOverlayStore();
  useEffect(() => {
    if (isOpen) {
      registerOverlay();
      return () => unregisterOverlay();
    }
  }, [isOpen, registerOverlay, unregisterOverlay]);

  const handleBack = useCallback(() => {
    setActiveConversation(null);
  }, [setActiveConversation]);

  const handleClose = useCallback(() => {
    closeChat();
  }, [closeChat]);

  const isInConversation = activeConversationId !== null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop overlay — prevents scroll-through, stays fixed */}
          <motion.div
            key="overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleClose}
            className={cn("fixed inset-0 z-40", "bg-black/40 backdrop-blur-sm")}
          />

          {/* Chat panel */}
          <motion.div
            key="panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "fixed z-50 overflow-hidden",
              // Mobile: full screen
              "inset-0 md:inset-auto",
              "md:bottom-0 md:right-6 md:w-[400px] md:h-[600px]",
              // Corners
              "rounded-t-[28px] md:rounded-[28px]",
              // Glass morphism
              "bg-dark-bg/95 dark:bg-dark-bg/95 bg-light-bg/95",
              "backdrop-blur-xl",
              "border border-white/[0.08] dark:border-white/[0.08] border-black/[0.08]",
              "shadow-2xl shadow-black/30 dark:shadow-black/50 shadow-black/[0.08]",
              "flex flex-col",
            )}
          >
            {/* Header */}
            <div
              className={cn(
                "flex items-center justify-between px-5 pt-5 pb-3 shrink-0",
                "border-b border-white/[0.06] dark:border-white/[0.06] border-black/[0.06]",
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full",
                    "bg-gold/15",
                  )}
                >
                  <MessageSquare
                    className="w-4 h-4 text-gold"
                    strokeWidth={1.8}
                  />
                </div>
                <h2
                  className={cn(
                    "text-base font-semibold",
                    "font-[var(--font-sf-display)]",
                    "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
                  )}
                >
                  Pesan
                </h2>
              </div>

              <button
                onClick={handleClose}
                aria-label="Tutup"
                className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full",
                  "text-dark-text-secondary/70 dark:text-dark-text-secondary/70 text-light-text-secondary/70",
                  "hover:bg-white/[0.06] dark:hover:bg-white/[0.06] hover:bg-black/[0.04]",
                  "transition-colors duration-150",
                )}
              >
                <X className="w-5 h-5" strokeWidth={1.8} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0">
              <AnimatePresence mode="wait">
                {isInConversation ? (
                  <ChatView key="chat-view" onBack={handleBack} />
                ) : (
                  <ConversationList key="conv-list" />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
